/**
 * Etat reel de la plateforme.
 *
 * Tout ce que ce module expose est MESURE, jamais suppose. La maquette
 * prevoyait aussi un nombre de fils d'emulation et un pourcentage de
 * chargement : ni l'un ni l'autre n'est lisible depuis le navigateur, ils sont
 * donc absents plutot qu'estimes. Un rail d'etat qui affiche des chiffres
 * inventes est pire qu'un rail plus court — il se lit comme une mesure.
 */

export interface Platform {
	/** Le navigateur sait executer du WebAssembly. */
	wasm: boolean;
	/** La page est isolee entre origines (COOP + COEP effectifs). */
	isolated: boolean;
	/** SharedArrayBuffer est reellement constructible. */
	sab: boolean;
	/** Contexte securise : HTTPS, ou localhost par exception. */
	secure: boolean;
	/** Coeurs logiques annonces par le navigateur, 0 si non communique. */
	cores: number;
}

export function readPlatform(): Platform {
	return {
		wasm: typeof WebAssembly === 'object' && typeof WebAssembly.instantiate === 'function',
		isolated: typeof crossOriginIsolated !== 'undefined' && crossOriginIsolated === true,
		// La seule preuve qui compte : le constructeur peut exister sans etre
		// utilisable, on tente donc une allocation d'un octet.
		sab: (() => {
			try {
				return typeof SharedArrayBuffer !== 'undefined' && new SharedArrayBuffer(1).byteLength === 1;
			} catch {
				return false;
			}
		})(),
		secure: typeof isSecureContext !== 'undefined' ? isSecureContext : false,
		cores: typeof navigator !== 'undefined' ? navigator.hardwareConcurrency || 0 : 0,
	};
}

/**
 * Memoire occupee par l'onglet, en octets.
 *
 * `performance.memory` est propre aux moteurs Chromium et ne mesure QUE le tas
 * JavaScript — pas la memoire du module WASM, qui est l'essentiel ici. Sur
 * Firefox et Safari elle n'existe pas. On renvoie donc null plutot qu'un
 * chiffre trompeur, et l'appelant masque la cellule.
 */
export function readHeap(): number | null {
	const memory = (performance as any)?.memory;
	if (!memory || typeof memory.usedJSHeapSize !== 'number') return null;
	return memory.usedJSHeapSize;
}

export function formatHeap(bytes: number, lang: 'fr' | 'en'): string {
	const mega = bytes / 1048576;
	if (mega >= 1024) {
		const giga = (mega / 1024).toFixed(2);
		return `${lang === 'fr' ? giga.replace('.', ',') : giga} ${lang === 'fr' ? 'Go' : 'GB'}`;
	}
	return `${Math.round(mega)} ${lang === 'fr' ? 'Mo' : 'MB'}`;
}
